/**
 * Process list panel component — displays a sortable, filterable table of
 * running processes inside a sandbox with kill capability.
 *
 * Designed for the Alternative layout bottom pane where vertical space is limited.
 * Toolbar + sticky header + scrollable body, all compact.
 *
 * @module components/terminal/ProcessesPanel
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { clsx } from 'clsx';
import { Loader2, AlertCircle, Search, X, ChevronUp, ChevronDown } from 'lucide-react';
import { useProcessList, type ProcessEntry } from './useProcessList';
import { useExecuteCommand } from '@/hooks';

interface ProcessesPanelProps {
  sandboxId: string;
  /** Optional sandbox group ID — routes operations through group-scoped URLs */
  sandboxGroupId?: string;
  /** Optional container name for pod sandboxes */
  containerName?: string;
  compact?: boolean;
  isActive: boolean;
}

type SortField = keyof Pick<
  ProcessEntry,
  'pid' | 'user' | 'cpu' | 'mem' | 'rss' | 'vsz' | 'stat' | 'time' | 'command'
>;
type SortDir = 'asc' | 'desc';

const SIGNALS = [
  { name: 'SIGTERM', num: 15 },
  { name: 'SIGKILL', num: 9 },
  { name: 'SIGHUP', num: 1 },
  { name: 'SIGINT', num: 2 },
] as const;

interface ColumnDef {
  field: SortField;
  label: string;
  shortLabel?: string;
  align: 'left' | 'right' | 'center';
  hideOnMobile?: boolean;
  width: string;
  format?: (v: ProcessEntry) => string;
}

const COLUMNS: ColumnDef[] = [
  { field: 'pid', label: 'PID', align: 'right', width: 'w-14' },
  { field: 'user', label: 'User', align: 'left', hideOnMobile: true, width: 'w-16' },
  { field: 'command', label: 'Command', align: 'left', width: 'flex-1 min-w-0' },
  { field: 'cpu', label: 'CPU%', align: 'right', width: 'w-14' },
  { field: 'mem', label: 'MEM%', align: 'right', width: 'w-14' },
  {
    field: 'rss',
    label: 'RSS',
    align: 'right',
    hideOnMobile: true,
    width: 'w-16',
    format: (p) => formatKB(p.rss),
  },
  {
    field: 'vsz',
    label: 'VSZ',
    align: 'right',
    hideOnMobile: true,
    width: 'w-16',
    format: (p) => formatKB(p.vsz),
  },
  { field: 'stat', label: 'State', align: 'center', hideOnMobile: true, width: 'w-12' },
  { field: 'time', label: 'Time', align: 'left', hideOnMobile: true, width: 'w-16' },
];

function formatKB(kb: number): string {
  if (kb === 0) return '0';
  if (kb < 1024) return `${kb}K`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)}M`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)}G`;
}

function compareProcesses(
  a: ProcessEntry,
  b: ProcessEntry,
  field: SortField,
  dir: SortDir
): number {
  let cmp: number;
  const av = a[field];
  const bv = b[field];
  if (typeof av === 'number' && typeof bv === 'number') {
    cmp = av - bv;
  } else {
    cmp = String(av).localeCompare(String(bv));
  }
  return dir === 'asc' ? cmp : -cmp;
}

export function ProcessesPanel({
  sandboxId,
  sandboxGroupId,
  containerName,
  isActive,
}: ProcessesPanelProps) {
  const { connectionState, processes, error, connect, disconnect, retry } = useProcessList(
    sandboxId,
    containerName,
    sandboxGroupId
  );
  const executeCommand = useExecuteCommand();

  const [sortField, setSortField] = useState<SortField>('cpu');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [filter, setFilter] = useState('');
  const [killMenuPid, setKillMenuPid] = useState<number | null>(null);
  const [killingPid, setKillingPid] = useState<number | null>(null);
  const killMenuRef = useRef<HTMLDivElement>(null);

  // Connect/disconnect based on isActive.
  // Use a small delay before connecting so React StrictMode's
  // mount-unmount-remount cycle completes before we open the WebSocket.
  useEffect(() => {
    if (!isActive) {
      console.log('[ProcessesPanel] Inactive — disconnecting exec stream');
      disconnect();
      return;
    }
    console.log('[ProcessesPanel] Active — connecting exec stream (WebSocket top)');
    const timer = setTimeout(() => {
      connect();
    }, 150);
    return () => {
      clearTimeout(timer);
      disconnect();
    };
  }, [isActive, connect, disconnect]);

  // Log connection state changes
  useEffect(() => {
    console.log(
      `[ProcessesPanel] mode=execStream state=${connectionState} processes=${processes.length}${error ? ` error="${error}"` : ''}`
    );
  }, [connectionState, processes.length, error]);

  // Close kill menu on outside click
  useEffect(() => {
    if (killMenuPid === null) return;
    const handler = (e: MouseEvent) => {
      if (killMenuRef.current && !killMenuRef.current.contains(e.target as Node)) {
        setKillMenuPid(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [killMenuPid]);

  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir(field === 'command' || field === 'user' ? 'asc' : 'desc');
      }
    },
    [sortField]
  );

  const handleKill = useCallback(
    async (pid: number, signal: number) => {
      setKillMenuPid(null);
      setKillingPid(pid);
      try {
        await executeCommand.mutateAsync({
          sandboxId,
          command: `kill -${signal} ${pid}`,
          containerName,
          sandboxGroupId,
        });
      } finally {
        // Clear killing state after a short delay so the UI feedback is visible
        setTimeout(() => setKillingPid(null), 1000);
      }
    },
    [executeCommand, sandboxId, sandboxGroupId, containerName]
  );

  const filterLower = filter.toLowerCase();
  const filtered = useMemo(() => {
    let result = processes;
    if (filterLower) {
      result = result.filter(
        (p) =>
          String(p.pid).includes(filterLower) ||
          p.user.toLowerCase().includes(filterLower) ||
          p.command.toLowerCase().includes(filterLower) ||
          p.stat.toLowerCase().includes(filterLower)
      );
    }
    return [...result].sort((a, b) => compareProcesses(a, b, sortField, sortDir));
  }, [processes, filterLower, sortField, sortDir]);

  // Loading state
  const isLoading =
    connectionState === 'disconnected' ||
    connectionState === 'connecting' ||
    (connectionState === 'connected' && processes.length === 0 && !error);

  if (!isActive) return null;

  if (isLoading && !error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-xs">Loading processes...</span>
        </div>
      </div>
    );
  }

  if (error && processes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-xs text-red-500">{error}</span>
          <button onClick={retry} className="ml-2 text-xs text-blue-500 hover:underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 overflow-hidden">
      {/* Toolbar: filter + count */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-200 dark:border-[#2a2a2a] flex-shrink-0">
        <div className="relative flex-1 max-w-[240px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
          <input
            type="text"
            placeholder="Filter processes..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full pl-6.5 pr-6 py-1 text-[11px] bg-gray-50 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30"
            style={{ paddingLeft: '26px' }}
          />
          {filter && (
            <button
              onClick={() => setFilter('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        <span className="text-[10px] text-gray-400 whitespace-nowrap">
          {filtered.length}
          {filter ? ` / ${processes.length}` : ''} processes
        </span>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-[11px] font-mono">
          {/* Sticky header */}
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-50 dark:bg-[#111] border-b border-gray-200 dark:border-[#2a2a2a]">
              {COLUMNS.map((col) => (
                <th
                  key={col.field}
                  className={clsx(
                    'px-2 py-1 font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none whitespace-nowrap',
                    col.align === 'right' && 'text-right',
                    col.align === 'center' && 'text-center',
                    col.align === 'left' && 'text-left',
                    col.hideOnMobile && 'hidden lg:table-cell'
                  )}
                  onClick={() => handleSort(col.field)}
                >
                  <span className="inline-flex items-center gap-0.5">
                    {col.shortLabel ?? col.label}
                    {sortField === col.field &&
                      (sortDir === 'asc' ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      ))}
                  </span>
                </th>
              ))}
              {/* Kill column header */}
              <th className="w-8 px-1 py-1" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((proc) => (
              <tr
                key={proc.pid}
                className={clsx(
                  'border-b border-gray-100 dark:border-[#2a2a2a]/50 hover:bg-gray-50 dark:hover:bg-[#161616] transition-colors',
                  killingPid === proc.pid && 'opacity-50'
                )}
              >
                {COLUMNS.map((col) => (
                  <td
                    key={col.field}
                    className={clsx(
                      'px-2 py-1 text-gray-600 dark:text-gray-300 whitespace-nowrap',
                      col.align === 'right' && 'text-right tabular-nums',
                      col.align === 'center' && 'text-center',
                      col.hideOnMobile && 'hidden lg:table-cell',
                      col.field === 'command' && 'max-w-0 truncate',
                      col.field === 'cpu' &&
                        proc.cpu > 50 &&
                        'text-red-500 dark:text-red-400 font-medium',
                      col.field === 'cpu' &&
                        proc.cpu > 20 &&
                        proc.cpu <= 50 &&
                        'text-yellow-600 dark:text-yellow-400',
                      col.field === 'mem' &&
                        proc.mem > 50 &&
                        'text-red-500 dark:text-red-400 font-medium',
                      col.field === 'mem' &&
                        proc.mem > 20 &&
                        proc.mem <= 50 &&
                        'text-yellow-600 dark:text-yellow-400'
                    )}
                    title={col.field === 'command' ? proc.command : undefined}
                  >
                    {col.format ? col.format(proc) : String(proc[col.field])}
                  </td>
                ))}
                {/* Kill button */}
                <td className="px-1 py-1 text-center relative">
                  <button
                    onClick={() => setKillMenuPid(killMenuPid === proc.pid ? null : proc.pid)}
                    disabled={killingPid === proc.pid}
                    className={clsx(
                      'p-0.5 rounded transition-colors',
                      killingPid === proc.pid
                        ? 'text-gray-300 dark:text-gray-600'
                        : 'text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                    )}
                    title="Kill process"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  {/* Signal dropdown */}
                  {killMenuPid === proc.pid && (
                    <div
                      ref={killMenuRef}
                      className="absolute right-0 top-full mt-0.5 z-20 bg-white dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-md shadow-lg py-0.5 min-w-[120px]"
                    >
                      {SIGNALS.map((sig) => (
                        <button
                          key={sig.num}
                          onClick={() => handleKill(proc.pid, sig.num)}
                          className="w-full text-left px-3 py-1 text-[11px] hover:bg-gray-100 dark:hover:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 flex justify-between items-center gap-2"
                        >
                          <span>{sig.name}</span>
                          <span className="text-gray-400 text-[10px]">({sig.num})</span>
                        </button>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={COLUMNS.length + 1}
                  className="px-3 py-6 text-center text-xs text-gray-400"
                >
                  {filter ? 'No processes match the filter.' : 'No processes found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
